class EditorialStoryShare extends HTMLElement {
  constructor() {
    super();

    /** @type {{ button: HTMLButtonElement | null, label: HTMLSpanElement | null, copyInput: HTMLTextAreaElement | null }} */
    this.refs = {
      button: null,
      label: null,
      copyInput: null
    };

    /** @type {AbortController | null} */
    this.abortController = null;

    /** @type {string} */
    this.defaultLabel = 'Share';

    /** @type {number | null} */
    this.labelResetTimeout = null;

  }

  connectedCallback() {
    this.#cacheRefs();

    if (!this.refs.button || !this.refs.label || !this.refs.copyInput) return;

    this.defaultLabel = this.refs.label.textContent?.trim() || 'Share';
    this.abortController = new AbortController();
    this.#bindEvents();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;

    if (this.labelResetTimeout) {
      window.clearTimeout(this.labelResetTimeout);
      this.labelResetTimeout = null;
    }
  }

  /**
   * @returns {{ title: string, text: string, url: string }}
   */
  getShareData() {
    return {
      title: this.dataset.shareTitle || '',
      text: this.dataset.shareText || '',
      url: this.dataset.shareUrl || window.location.href
    };
  }

  async share() {
    const shareData = this.getShareData();

    if (!shareData.url) return false;

    if (navigator.share) {
      await navigator.share(shareData);
      return true;
    }

    return this.copyLink();
  }

  async copyLink() {
    const { url } = this.getShareData();

    if (!url || !this.refs.copyInput) return false;

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }

    this.refs.copyInput.value = url;
    this.refs.copyInput.focus();
    this.refs.copyInput.select();
    this.refs.copyInput.setSelectionRange(0, this.refs.copyInput.value.length);

    const isCopied = document.execCommand('copy');

    this.refs.copyInput.blur();

    if (!isCopied) {
      throw new Error('Copy command failed');
    }

    return true;
  }

  resetLabel() {
    if (!this.refs.label) return;

    this.refs.label.textContent = this.defaultLabel;
  }

  #cacheRefs() {
    this.refs = {
      button: this.querySelector('[data-share-ref="button"]'),
      label: this.querySelector('[data-share-ref="label"]'),
      copyInput: /** @type {HTMLTextAreaElement | null} */ (this.querySelector('[data-share-ref="copy-input"]'))
    };
  }

  #bindEvents() {
    if (!this.abortController || !this.refs.button) return;

    this.refs.button.addEventListener('click', (event) => this.#handleClick(event), {
      signal: this.abortController.signal
    });
  }

  /**
   * @param {MouseEvent} event
   */
  async #handleClick(event) {
    event.preventDefault();

    try {
      const didShare = await this.share();

      if (didShare && !navigator.share) {
        this.#setTemporaryLabel('Copied');
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;

      try {
        const didCopy = await this.copyLink();

        if (didCopy) {
          this.#setTemporaryLabel('Copied');
        }
      } catch (_copyError) {
        this.resetLabel();
      }
    }
  }

  /**
   * @param {string} label
   */
  #setTemporaryLabel(label) {
    if (!this.refs.label) return;

    this.refs.label.textContent = label;

    if (this.labelResetTimeout) {
      window.clearTimeout(this.labelResetTimeout);
    }

    this.labelResetTimeout = window.setTimeout(() => {
      this.resetLabel();
      this.labelResetTimeout = null;
    }, 2000);
  }
}

if (!customElements.get('editorial-story-share')) {
  customElements.define('editorial-story-share', EditorialStoryShare);
}
